import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Info,
  Sparkles,
  Baby,
  Landmark,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import WhatIfSimulator from "@/components/simulator/WhatIfSimulator";
import IntergenerationalDebtPanel from "@/components/simulator/IntergenerationalDebtPanel";
import PublicSectorDashboard from "@/components/simulator/PublicSectorDashboard";
import ErrorBoundary from "@/components/ErrorBoundary";
import { getProject } from "@/lib/profiles";
import { getLatestAnalysis } from "@/lib/analyses";

export default function Simulator() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [proj, an] = await Promise.all([
        getProject(projectId),
        getLatestAnalysis(projectId),
      ]);
      if (!proj) {
        toast.error("Projet introuvable.");
        navigate("/app/dashboard", { replace: true });
        return;
      }
      setProject(proj);
      setAnalysis(an);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Impossible de charger la simulation."
      );
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div
        className="flex min-h-[50vh] items-center justify-center"
        data-testid="simulator-loading"
      >
        <div className="text-sm text-muted-foreground">Chargement…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="simulator-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Analyses avancées · Outils déterministes (zéro LLM)
        </div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">
          Simulateur vitaliste
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          {project?.name}
          {project?.fiscal_year && ` · Exercice ${project.fiscal_year}`} —
          Trois outils exploratoires pour simuler l'impact vitaliste de
          réformes, projeter l'hypothèque intergénérationnelle de la dette et
          qualifier le rendement public par secteur.
        </p>
      </div>

      {!analysis ? (
        <Card className="border-dashed" data-testid="simulator-no-analysis">
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <Info className="h-10 w-10 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Analyse requise</h3>
            <p className="max-w-md text-sm text-muted-foreground">
              Lancez d'abord l'analyse vitaliste (étape 3 du wizard) pour
              nourrir les simulateurs.
            </p>
            <Button
              onClick={() =>
                navigate(`/app/projects/${projectId}/wizard/confirm`)
              }
              data-testid="simulator-go-confirm-btn"
            >
              Aller à l'étape 3
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="whatif" data-testid="simulator-tabs">
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger
              value="whatif"
              className="gap-1.5"
              data-testid="tab-whatif"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Simulateur "Et si ?"
            </TabsTrigger>
            <TabsTrigger
              value="intergen"
              className="gap-1.5"
              data-testid="tab-intergen"
            >
              <Baby className="h-3.5 w-3.5" />
              Dette intergénérationnelle
            </TabsTrigger>
            <TabsTrigger
              value="sectors"
              className="gap-1.5"
              data-testid="tab-sectors"
            >
              <Landmark className="h-3.5 w-3.5" />
              Tableau de bord gestionnaire
            </TabsTrigger>
          </TabsList>

          <TabsContent value="whatif" className="pt-4">
            <ErrorBoundary label="whatif">
              <WhatIfSimulator analysis={analysis} />
            </ErrorBoundary>
          </TabsContent>
          <TabsContent value="intergen" className="pt-4">
            <ErrorBoundary label="intergen">
              <IntergenerationalDebtPanel analysis={analysis} />
            </ErrorBoundary>
          </TabsContent>
          <TabsContent value="sectors" className="pt-4">
            <ErrorBoundary label="sectors">
              <PublicSectorDashboard analysis={analysis} />
            </ErrorBoundary>
          </TabsContent>
        </Tabs>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
        <Button
          variant="outline"
          onClick={() =>
            navigate(`/app/projects/${projectId}/wizard/results`)
          }
          data-testid="simulator-back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour au dashboard
        </Button>
      </div>
    </div>
  );
}
